export const SCORE_COLORS = {
  excellent: '#18B368',
  good: '#1570EF',
  average: '#EDA12F',
  low: '#E74444',
} as const;

export const SCORE_COLOR_BG = {
  excellent: '#18B36810',
  good: '#1570EF10',
  average: '#EDA12F10',
  low: '#E7444410',
} as const;

export type ScoreLevel = keyof typeof SCORE_COLORS;

export const getScoreLevel = (score: number): ScoreLevel => {
  if (score >= 90) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 50) return 'average';
  return 'low';
};

export const getScoreColor = (score: number): string =>
  SCORE_COLORS[getScoreLevel(score)];

export const getScoreBgColor = (score: number): string =>
  SCORE_COLOR_BG[getScoreLevel(score)];

export const getScoreBgClass = (score: number): string => {
  const level = getScoreLevel(score);
  if (level === 'excellent') return 'bg-[#18B368]';
  if (level === 'good') return 'bg-[#1570EF]';
  if (level === 'average') return 'bg-[#EDA12F]';
  return 'bg-[#E74444]';
};

export const getScoreTextClass = (score: number): string => {
  const level = getScoreLevel(score);
  if (level === 'excellent') return 'text-[#18B368]';
  if (level === 'good') return 'text-[#1570EF]';
  if (level === 'average') return 'text-[#EDA12F]';
  return 'text-[#E74444]';
};

const BADGE_TO_LEVEL: Record<string, ScoreLevel> = {
  HIGH: 'excellent',
  MEDIUM: 'good',
  LOW: 'average',
};

const getBadgeScore = (badge?: string | number | null): number | null => {
  if (typeof badge === 'number' && Number.isFinite(badge)) {
    return badge;
  }

  if (typeof badge !== 'string') {
    return null;
  }

  const parsedScore = Number.parseInt(badge.replace(/[^0-9]/g, ''), 10);
  return Number.isNaN(parsedScore) ? null : parsedScore;
};

const getBadgeLevel = (badge?: string | number | null): ScoreLevel | null => {
  const badgeScore = getBadgeScore(badge);

  if (badgeScore !== null) {
    return getScoreLevel(badgeScore);
  }

  if (typeof badge !== 'string') {
    return null;
  }

  return BADGE_TO_LEVEL[badge.toUpperCase()] ?? null;
};

export const getBadgeColor = (badge?: string | number | null): string => {
  const level = getBadgeLevel(badge);
  return level ? SCORE_COLORS[level] : SCORE_COLORS.low;
};

export const getBadgeBgClass = (badge?: string | number | null): string => {
  const level = getBadgeLevel(badge);
  if (level === 'excellent') return 'bg-[#18B368]';
  if (level === 'good') return 'bg-[#1570EF]';
  if (level === 'average') return 'bg-[#EDA12F]';
  return 'bg-[#E74444]';
};
